<section class="page-shell">
  <header class="page-header">
    <div>
      <h4>Anomalies de signature</h4>
      <p>Contrôle les avancements en signature Minotaure, la GED et le suivi Signature Batch.</p>
    </div>
  </header>

  <article class="panel dbw-card dbw-card--xl filters">
    <oui-range-date-picker class="period" label="Période" [control]="period"></oui-range-date-picker>
    <oui-textfield class="time" label="Heure de début" type="time" [value]="startTime()" (valueChange)="onStartTimeChange($event ? $event.toString() : '')"></oui-textfield>
    <oui-textfield class="time" label="Heure de fin" type="time" [value]="endTime()" (valueChange)="onEndTimeChange($event ? $event.toString() : '')"></oui-textfield>
    <oui-button (click)="load()" [disabled]="loading()">Rechercher</oui-button>
    <oui-button (click)="exportCsv()" [disabled]="exporting()">Exporter CSV</oui-button>
    <oui-button (click)="loadExtraDocuments()" [disabled]="loadingExtraDocuments()">Documents supplémentaires</oui-button>
    <oui-button (click)="requestIndexing()" [disabled]="requestingIndexing()">Indexer Universign</oui-button>
    <label>Filtre <select [value]="filter()" (change)="onFilterChange($any($event.target).value)"><option value="all">Toutes</option><option value="ged-missing">GED introuvable</option><option value="signature-only">Signature non suivie</option></select></label>
    @if (canUpdateState() || canCancelGed() || canCancelUniversign() || canConfirmUniversign()) { <oui-textfield class="reason" label="Motif de correction" [value]="reason()" (valueChange)="onReasonChange($event ? $event.toString() : '')"></oui-textfield> }
  </article>

  @if (error()) { <dbw-state-card appearance="error" title="Traitement impossible"><p>{{ error() }}</p></dbw-state-card> }
  @if (loading()) { <dbw-state-card appearance="loading" [compact]="true"><p>Contrôle des anomalies en cours.</p></dbw-state-card> }

  @if (indexStatus(); as index) {
    @if (!index.isCovered) {
      <dbw-state-card appearance="error" title="Universign non indexé sur cette période">
        <p>
          Les correspondances Universign proviennent d'un index local. Cette période n'étant pas
          entièrement indexée, une colonne Universign vide ne signifie pas qu'il n'existe aucune
          transaction — seulement qu'on ne l'a pas encore parcourue.
        </p>
        <p>
          @if (indexingInProgress()) {
            Indexation en cours. Elle peut durer plusieurs minutes : relancez la recherche ensuite.
          } @else {
            Utilisez « Indexer Universign » pour lancer le parcours de cette période en tâche de fond.
          }
        </p>
      </dbw-state-card>
    }
  }

  @if (checkFailures().total > 0) {
    <dbw-state-card appearance="error" title="Résultat incomplet">
      <p>
        {{ checkFailures().total }} contrôle(s) n'ont pas pu être effectués : le tableau ci-dessous
        n'est pas exhaustif et un faible nombre d'anomalies ne signifie pas que tout est conforme.
      </p>
      <p>
        @if (checkFailures().signature > 0) {
          {{ checkFailures().signature }} via le service de suivi de signature (indisponible ou accès refusé).
        }
        @if (checkFailures().ged > 0) {
          {{ checkFailures().ged }} via la GED (lecture du document impossible).
        }
      </p>
    </dbw-state-card>
  }

  @if (report(); as result) {
    <article class="panel dbw-card dbw-card--xl">
      <div class="metrics">
        <span>DER analysés : <strong>{{ result.derCandidateCount }}</strong></span>
        <span>Recueil analysés : <strong>{{ result.recueilCandidateCount }}</strong></span>
        <span>QPI analysés : <strong>{{ result.qpiCandidateCount }}</strong></span>
        <span>Anomalies : <strong>{{ metrics().filtered }}</strong> / {{ metrics().total }}</span>
        <span>Investisseurs concernés : <strong>{{ metrics().affectedInvestors }}</strong></span>
        <span>Introuvables en GED : <strong>{{ metrics().gedMissing }}</strong></span>
        <span>Signatures non suivies : <strong>{{ metrics().signatureNotTracked }}</strong></span>
        @if (checkFailures().total > 0) {
          <span class="metric-warning">Contrôles impossibles : <strong>{{ checkFailures().total }}</strong></span>
        }
      </div>

      @if (groupedAnomalies().length === 0) {
        <p>Aucune anomalie détectée sur cette période.</p>
      } @else {
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Investisseur</th>
                <th>Consultant</th>
                <th>Catégorie</th>
                <th class="col-state">État</th>
                <th class="col-date">Dernière modification Proxeema</th>
                <th class="col-date">Envoi en signature</th>
                <th>GED</th>
                <th>Suivi signature</th>
                <th>Universign</th>
                <th class="col-motif">Motif</th>
                <th>Actions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (group of groupedAnomalies(); track group.investorId) {
                @for (anomaly of group.anomalies; track anomaly.category + anomaly.documentId; let first = $first) {
                  <tr>
                    @if (first) {
                      <td [attr.rowspan]="group.anomalies.length">
                        <strong>{{ group.profile?.investorLastName }} {{ group.profile?.investorFirstName }}</strong>
                        <br><span class="code">{{ group.profile?.investorCode || group.investorId }}</span>
                      </td>
                      <td [attr.rowspan]="group.anomalies.length">
                        @if (group.profile?.consultantCode) {
                          <strong>{{ group.profile?.consultantLastName }} {{ group.profile?.consultantFirstName }}</strong>
                          <br><span class="code">{{ group.profile?.consultantCode }}</span>
                        } @else { — }
                      </td>
                    }

                    <td>{{ anomaly.trackedByAvancement ? anomaly.category : 'Hors avancement' }}</td>

                    <td class="col-state">
                      {{ anomaly.stateLabel }} <span class="code">({{ anomaly.stateCode }})</span>
                    </td>

                    <td class="col-date">{{ anomaly.lastChangeDate | date: 'dd/MM/yyyy HH:mm' }}</td>

                    <td class="col-date">
                      {{ (anomaly.gedTimestamp || anomaly.gedCreationDate)
                          ? ((anomaly.gedTimestamp || anomaly.gedCreationDate) | date: 'dd/MM/yyyy HH:mm')
                          : '—' }}
                    </td>

                    <td class="document-id">
                      <span class="badge" [class.danger]="!anomaly.gedFound">{{ anomaly.gedFound ? 'Trouvé' : 'Introuvable' }}</span>
                      @if (anomaly.gedFound) {
                        <small>
                          {{ anomaly.gedDesignation || anomaly.documentId }}<br>
                          Signature : {{ anomaly.gedSignatureState || '—' }}<br>
                          Validité : {{ anomaly.gedValidityState || '—' }}<br>
                          SignatureId : {{ anomaly.gedSignatureId || '—' }}
                        </small>
                      }
                    </td>

                    <td>
                      <span class="badge" [class.danger]="!anomaly.signatureTrackingFound">{{ anomaly.signatureTrackingFound ? 'Suivi' : 'Non suivi' }}</span>
                    </td>

                    <td>
                      @if (anomaly.universignMatches.length === 0) { — } @else {
                        <details class="matches">
                          <summary>{{ anomaly.universignMatches.length }} correspondance(s)</summary>
                          <table>
                            <thead><tr><th>Transaction</th><th>Statut</th><th>Créé le</th><th></th></tr></thead>
                            <tbody>
                              @for (match of anomaly.universignMatches; track match.transactionId) {
                                <tr>
                                  <td><span class="code">{{ match.transactionId }}</span><br><small>{{ match.fileName }}</small></td>
                                  <td>{{ match.status }}</td>
                                  <td>{{ match.creationDate ? (match.creationDate | date: 'dd/MM/yyyy HH:mm') : '—' }}</td>
                                  <td>
                                    @if (match.status.toLowerCase() === 'completed') {
                                      <oui-button (click)="downloadUniversignDocument(anomaly, match.transactionId, match.fileName)" [disabled]="actionInProgress() !== undefined">Télécharger</oui-button>
                                      @if (canConfirmUniversign()) {
                                        <oui-button (click)="confirmUniversign(anomaly, match.transactionId)" [disabled]="actionInProgress() !== undefined">Corriger (GED)</oui-button>
                                      }
                                    } @else if (canCancelUniversign()) {
                                      <oui-button (click)="cancelUniversign(anomaly, match.transactionId)" [disabled]="actionInProgress() !== undefined">Annuler</oui-button>
                                    }
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </details>
                      }
                    </td>

                    <td class="col-motif">{{ anomaly.reason }}</td>

                    <td>
                      @if (anomaly.trackedByAvancement) {
                        @if (canUpdateState()) {
                          <select #state>
                            <option [value]="anomaly.stateValue">Conserver</option>
                            @for (option of stateOptionsFor(anomaly); track option.value) {
                              <option [value]="option.value">{{ option.label }} — {{ option.code }}</option>
                            }
                          </select>
                          <oui-button (click)="updateState(anomaly, state.value)" [disabled]="actionInProgress() !== undefined">Modifier</oui-button>
                        }
                      } @else {
                        <small>Pas d'avancement suivi</small>
                      }
                      @if (canCancelGed() && anomaly.gedFound) {
                        <oui-button (click)="cancelGed(anomaly)" [disabled]="actionInProgress() !== undefined">Annuler GED</oui-button>
                      }
                    </td>

                    @if (first) {
                      <td [attr.rowspan]="group.anomalies.length">
                        <a [routerLink]="appRoutePaths.proxeema.signaturesInvestisseurDetail(group.investorId)">Voir la fiche</a>
                        @if (backofficeUrl(group.profile); as backoffice) {
                          <br><a [href]="backoffice" target="_blank" rel="noopener">Backoffice</a>
                        }
                      </td>
                    }
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }
    </article>
  }
</section>
